// Persiste ordem custom + cor accent das abas em localStorage por usuário.
// Cada lugar que usa abas (top nav, sub-abas de módulo etc) passa uma chave
// única (`tabPrefs.<escopo>`). Reset volta pra ordem padrão.
//
// Como as prefs ficam só no localStorage, são per-browser/per-user. Sem
// migration ou esforço de backend. Quando virar enterprise, migrar pra
// Cognito attribute.
import { useState } from 'react';

export interface TabPrefs<T extends string> {
  ordem: T[];
  cores: Record<string, string>;  // chave da aba → hex (#RRGGBB)
}

export const PALETA_CORES: (string | null)[] = [
  '#020D7F', '#7C3AED', '#0a7f4a', '#B07500',
  '#B91626', '#0930D0', '#7A5500', '#5A22B7',
  null /* sem cor */,
];

export function useTabsPrefs<T extends string>(storageKey: string, padrao: T[]): {
  ordem: T[]; cores: Record<string, string>;
  reordenar: (de: number, pra: number) => void;
  setCor: (aba: T, cor: string | null) => void;
  reset: () => void;
} {
  const [prefs, setPrefs] = useState<TabPrefs<T>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const p = JSON.parse(raw) as TabPrefs<T>;
        // Sanity: garante que todas as abas padrão existem (em caso de adicionar
        // aba nova depois de já ter prefs salvas) e remove abas que não existem
        // mais. Mantém a ordem custom + acrescenta novas no fim.
        const validas = (p.ordem || []).filter(o => padrao.includes(o));
        const faltando = padrao.filter(o => !validas.includes(o));
        return { ordem: [...validas, ...faltando], cores: p.cores || {} };
      }
    } catch { /* localStorage indisponível ou JSON corrompido — segue com padrão */ }
    return { ordem: padrao, cores: {} };
  });

  function persist(novo: TabPrefs<T>) {
    setPrefs(novo);
    try { localStorage.setItem(storageKey, JSON.stringify(novo)); } catch { /* ignora */ }
  }
  function reordenar(de: number, pra: number) {
    if (de === pra || de < 0 || pra < 0) return;
    const novaOrdem = [...prefs.ordem];
    const [moved] = novaOrdem.splice(de, 1);
    novaOrdem.splice(pra, 0, moved);
    persist({ ...prefs, ordem: novaOrdem });
  }
  function setCor(aba: T, cor: string | null) {
    const novasCores = { ...prefs.cores };
    if (cor) novasCores[aba] = cor; else delete novasCores[aba];
    persist({ ...prefs, cores: novasCores });
  }
  function reset() { persist({ ordem: padrao, cores: {} }); }

  return { ordem: prefs.ordem, cores: prefs.cores, reordenar, setCor, reset };
}
